/**
 * Code of Conduct types + filtering for the medicolegal Report Builder.
 *
 * MDRS-139: the Report Builder no longer auto-inserts a Code of Conduct
 * declaration (removed in MDRS-140). Instead, experts browse the correct
 * wording in the CoC panel, filter by category, and copy the paragraph they
 * need into the draft.
 *
 * The declaration text is served by the backend
 * (GET /report-generator/code-of-conduct, seeded from the Confluence
 * "Code of Conduct" table) and fetched via `useCodeOfConduct`. This file holds
 * only the shared types and the client-side category filter — the content
 * itself is no longer bundled here.
 */

export type CocEntry = {
  /** Stable id (the backend slug) — used as React key and for tests. */
  id: string;
  /**
   * Grouping key from the Confluence table's first column — either a
   * jurisdiction ("New South Wales", "Victoria"…) or a service where the row
   * isn't tied to one state ("Comcare", "DVA claims", "Family law"). Drives the
   * filter dropdown.
   */
  category: string;
  /** The specific service line this declaration is written for. */
  label: string;
  /**
   * The kind of services the declaration covers (Confluence "Kind of services"
   * column) — e.g. "public liability / civil liability / professional
   * indemnity / medical negligence". Empty when the row isn't tied to a
   * specific service kind.
   */
  serviceKind: string;
  /** The first-person declaration the expert copies into the report. */
  text: string;
};

/**
 * Distinct categories in table order (no duplicates) — the option list for the
 * filter dropdown. First appearance wins, so ordering follows `entries`.
 */
export const getCocCategories = (entries: CocEntry[]): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const e of entries) {
    if (!seen.has(e.category)) {
      seen.add(e.category);
      out.push(e.category);
    }
  }
  return out;
};

/**
 * Filter entries by a multi-category selection (MDRS-139 panel). An empty
 * selection returns everything; otherwise returns the entries whose category is
 * selected. Result order follows `entries` and entries are never duplicated.
 */
export const getCodeOfConductEntriesForCategories = (
  categories: string[],
  entries: CocEntry[]
): CocEntry[] => {
  if (categories.length === 0) return entries;
  const selected = new Set(categories);
  return entries.filter((e) => selected.has(e.category));
};
